import { useIsMobile } from "@/hooks/use-mobile";
import { useEffect, useState } from "react";
import { routes } from "@/routes/utils";
import { useNavigate, useParams } from "react-router-dom";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, Switch } from "@/components/ui";
import { useGetTaskCategoriesByProject } from "@/services";
import { MoreVertical, Edit } from "lucide-react";

const useCategory = () => {
  const isMobile = useIsMobile();
  const { id, action } = useParams();
  const [globalFilter, setGlobalFilter] = useState("");
  const navigate = useNavigate();
  const isView = action == routes.view;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const { data: categoryList, isLoading: isCategoryListLoading } =
    useGetTaskCategoriesByProject({ projectId: id as string });

  // Define Table Columns
  const columns = [
    {
      accessorKey: "taskCategoryId",
      header: "ID",
      accessorFn: (row: any) => String(row?.taskCategoryId || ""),
    },
    {
      accessorKey: "title",
      header: "Category Name",
      cell: (row: any) => {
        return row?.row?.original?.title ? (
          <span
            className="text-secondary border-b-1 border-secondary group-hover:border-secondary cursor-pointer font-[500]"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              navigate(`${row.row?.original.taskCategoryId}/view`);
            }}
          >
            {String(row.row?.original.title)}
          </span>
        ) : (
          <div className="px-3">{""}</div>
        );
      },
    },
    {
      accessorKey: "subCategoryCount",
      header: "# Sub Category",
      cell: (row: any) => {
        return row?.row?.original?.subCategoryCount ? (
          <span
            className="text-secondary border-b-1 border-secondary group-hover:border-secondary cursor-pointer font-[500]"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              navigate(`${row.row?.original.taskCategoryId}/view`);
              // queryClient.cancelQueries({queryKey: ["sub-categories"]})
            }}
          >
            {String(row.row?.original.subCategoryCount)}
          </span>
        ) : (
          <div className="px-3">{""}</div>
        );
      },
    },
    {
      accessorKey: "description",
      header: "Description",
      accessorFn: (row: any) => String(row?.description || ""),
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }: any) => {
        return row.original.status ? (
          <div>
            <Switch
              onChange={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            />
          </div>
        ) : (
          <div className="px-3">{""}</div>
        );
      },
    },
    ...isView ? [] : [{
      id: "actions",
      header: "Actions",
      cell: (row: any) => (
        <DropdownMenu>
          <DropdownMenuTrigger>
            <MoreVertical className="w-5 h-5 cursor-pointer" />
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className="bg-white border-0 shadow-md"
          >
            <DropdownMenuItem
              className="flex items-center gap-2 cursor-pointer"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                navigate(`${row.row?.original?.taskCategoryId}/edit`);
              }}
            >
              <Edit className="w-4 h-4" />
              Edit
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    }],
  ];

  const onAdd = () => {
    navigate(`${routes.add}`);
  }

  return {
    onAdd,
    isMobile,
    globalFilter,
    setGlobalFilter,
    columns,
    categoryList,
    isCategoryListLoading,
    isView,
  };
};

export default useCategory;

